/**
 * S5, onboarding: the first build, live. `/new/building`
 *
 * The emotional payoff, and the one screen in the product that is designed as
 * a narrated sequence rather than a page. Everything here is in service of one
 * argument: a person who has never deployed anything watches six plain
 * sentences happen, and then gets a web address that works.
 *
 * The design decisions worth defending, because a later pass will be tempted
 * to undo each of them:
 *
 * 1. The heading changes with the phase and nothing else moves with it.
 *    Setting up your app, then Building your app, then the project's own name
 *    and the word live. Screen spec 6.3, verbatim.
 *
 * 2. The checklist never reorders, never collapses, and completed steps stay
 *    on screen, including when a later step fails. "Your database and file
 *    storage are ready, only the build didn't finish" is a materially
 *    different sentence from "it failed", and the ledger has to be able to
 *    prove it.
 *
 * 3. The database and the file storage are created silently and announced
 *    afterwards, which is brief rule F2 and the place this product teaches the
 *    word Connection. Nobody was asked to choose a database, so nobody is told
 *    one is being created until it exists.
 *
 * 4. The celebration is not confetti. The web address block makes one 400ms
 *    settle and then the screen is still. Loop celebrates with confetti
 *    because Loop is a school. SV Cloud celebrates with a URL that works.
 *
 * 5. A failure lands on this page, in the error block, under the list that
 *    shows what did work. It is never a toast. A toast can leave on its own,
 *    and this is the one message a person must not be able to miss.
 *
 * 6. The build output is an Advanced detail, and it sits BELOW the web address
 *    rather than above it. Nothing is allowed to push the payoff down at the
 *    one moment the whole product is arguing for itself. In Simple it is one
 *    click behind the disclosure's own trigger, which is the sanctioned
 *    mechanism: gating the disclosure on the mode would short circuit its
 *    Simple half and leave Simple with no way to reach the log at all.
 *
 * The whole sequence is clock derived, from the one engine in lib/mock: a
 * refresh mid build keeps the build correct, and a backgrounded tab does not
 * desync, which is exactly what breaks tick based fakes during a live demo.
 * Speed comes from the demo dock, so nobody waits forty seconds in front of an
 * audience and nobody has to describe the failure screen.
 *
 * States, all deep linkable so a reviewer can open them side by side:
 *   (default)             the happy sequence
 *   ?state=failed         the build stops, the error block appears in place
 *   ?state=slow           the "you can leave this page" escape hatch
 *   ?state=live           the revisit: straight to the payoff
 *   ?state=handoff        the run settled but the app is not live yet, so
 *                         the setup hand-off block stands in for the payoff
 * The demo dock's "Next build fails" switch arms the same failure.
 */

import { Fragment, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router';

import { AdvancedDisclosure } from '../../components/patterns/AdvancedDisclosure';
import { AsyncNarration } from '../../components/patterns/AsyncNarration';
import { CopyField } from '../../components/patterns/CopyField';
import { ErrorBlock } from '../../components/patterns/ErrorBlock';
import { Notice } from '../../components/patterns/Notice';
import { SegmentedControl } from '../../components/patterns/SegmentedControl';
import { LONG_RUNNING_MS, StepLedger, type NarrationStep } from '../../components/patterns/StepLedger';
import { runLedgerSteps } from '../../lib/run-ledger';
import { StepDots } from '../../components/patterns/StepDots';
import { Button } from '../../components/primitives/Button';

import { useAccount } from '../../lib/auth/use-app-data';
import { useTicker } from '../../lib/hooks/use-ticker';
import { t } from '../../lib/i18n/t';
import { captureEvent } from '../../lib/analytics/posthog';
import { ONBOARDING_STEPS } from '../../lib/mock/onboarding';
import { FIRST_BUILD } from '../../lib/mock/scripts';
import { NEW_PROJECT_DRAFT } from '../../lib/mock/seed';
import { stateOf } from '../../lib/mock/simulate';
import { useDemoStore } from '../../lib/mock/store';
import { connectProject, getProject, getRun, type Run } from '../../lib/real-api';
import type { LogLine, Project, TimelineOutcome } from '../../lib/types';
import { fullSetupPrompt, svgitProjectPrompt } from '../../lib/setup-prompt';
import { useAddressAnswers } from '../../lib/hooks/use-address-answers';

import '../../styles/screens/first-build.css';

const USE_MOCK = import.meta.env.VITE_API_MODE === 'mock';

/** One run of the first build at a time, on one well known id. */
const TIMELINE_ID = 'first-build';

/** The step the heading changes on. Read from the script, never counted. */
const BUILD_INDEX = FIRST_BUILD.steps.findIndex((step) => step.key === 'build');

/** Platform-side failures, not the user's: worth one silent auto-retry
 * before ever showing them a failure screen. Codes outside this set (bad
 * repo state, a taken subdomain, ...) would just fail identically again. */
const TRANSIENT_RUN_ERROR_CODES = new Set([
  'GITHUB_API_FAILURE',
  'CLOUDFLARE_API_FAILURE',
  'DATABASE_PROVISIONING_FAILED',
]);

/** What ConnectCode hands FirstBuild via router state so a retry can re-run
 * the exact same connect call. Absent on a reload/direct link, in which case
 * retry degrades to "go start over" instead of erroring. */
interface RetryInput {
  name: string;
  /** 'svgit' retries without a repository; absent means GitHub. */
  codeHost?: 'svgit';
  repoFullName?: string;
  githubInstallationId?: string;
}

type ScreenState = 'building' | 'failed' | 'slow' | 'live' | 'handoff';

function readState(search: string): ScreenState {
  const value = new URLSearchParams(search).get('state');
  if (value === 'failed' || value === 'build-failed') return 'failed';
  if (value === 'slow') return 'slow';
  if (value === 'live' || value === 'already-live') return 'live';
  if (value === 'handoff' || value === 'finish-setup') return 'handoff';
  return 'building';
}

/** Elapsed build time, in the gutter of the log. Mono, tabular, unscaled. */
function stamp(ms: number): string {
  const total = Math.round(ms / 1000);
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

/** The same lines the panel shows, as text, for the error block's disclosure. */
function asText(lines: LogLine[]): string {
  return lines.map((line) => `${stamp(line.at)}  ${line.line}`).join('\n');
}

/** Real mode: poll GET /runs/:id, then GET /projects (by slug) until the
 * project is actually live, see this file's plan note on why "your app is
 * live" can't be said the moment the run settles. */
/* ------------------------------------------------- the setup hand-off block

 * What a person sees when the run has settled but the app is not live yet:
 * the web address that exists, and the work that is still theirs to do. It
 * lives here rather than inline in RealFirstBuild because the mock build
 * reaches this same state through ?state=handoff, and a design review of copy
 * this fiddly is worth nothing if the two are separate markup that can drift.
 *
 * The one-shot prompt is first and the three numbered steps stay under it:
 * the steps are three things that have to be done in order, which is what a
 * coding agent is good at, but somebody who would rather run the commands
 * themselves should not have to read a prompt to find out what they are.
 */
function SetupHandoff({
  webAddress,
  repoFullName,
  cloneMethod,
  onCloneMethod,
  onGoToProject,
  svgitSlug,
  setupFirst,
}: {
  webAddress: string | null;
  repoFullName: string;
  cloneMethod: 'git' | 'gh';
  onCloneMethod: (value: 'git' | 'gh') => void;
  onGoToProject: () => void;
  /** Set for a project whose code is stored with SV Cloud: one prompt, nothing else to do. */
  svgitSlug?: string;
  /** A brand-new owner: the prompt also sets up this computer, so no separate step is offered. */
  setupFirst?: boolean;
}) {
  if (svgitSlug) {
    return (
      <section className="fbuild__payoff" aria-label={t('Finish setting up')}>
        {webAddress ? <CopyField label={t('Web address')} value={webAddress} size="lg" /> : null}

        <div className="fbuild__cmd-group fbuild__cmd-group--all">
          <div className="fbuild__cmd-head">
            <span className="fbuild__cmd-title">{t('Paste this into your coding agent')}</span>
          </div>
          {/* English in every language; lib/setup-prompt.ts says why. */}
          <CopyField label={t('Project prompt')} value={svgitProjectPrompt(svgitSlug, setupFirst)} />
          <p className="fbuild__cmd-note">
            {t("That's the last thing you need this page for. Your agent can do everything else.")}
          </p>
          {setupFirst ? (
            <p className="fbuild__cmd-note">
              {t('This one prompt also sets up this computer, so there is nothing to do first.')}
            </p>
          ) : (
            <p className="fbuild__cmd-note">
              {t('First time on this computer? Set it up once and your agent can reach SV Cloud.')}{' '}
              <Link to="/setup">{t('Set up your computer')}</Link>
            </p>
          )}
        </div>

        <div className="fbuild__actions">
          <Button variant="outline" onClick={onGoToProject}>
            {t('Go to your project')}
          </Button>
        </div>
      </section>
    );
  }

  return (
    <section className="fbuild__payoff" aria-label={t('Finish setting up')}>
      {webAddress ? <CopyField label={t('Web address')} value={webAddress} size="lg" /> : null}

      <p className="fbuild__push">
        {t(
          "We committed your starter code. If your repository was empty, it's building right now and this page will say so when it's live.",
        )}
      </p>

      <div className="fbuild__cmd-group fbuild__cmd-group--all">
        <div className="fbuild__cmd-head">
          <span className="fbuild__cmd-title">{t('Paste this into your coding agent')}</span>
        </div>
        {/* English in every language; lib/setup-prompt.ts says why. */}
        <CopyField label={t('Full setup prompt')} value={fullSetupPrompt(repoFullName)} />
        <p className="fbuild__cmd-note">{t('It does all three steps below. Or do them yourself:')}</p>
      </div>

      <div className="fbuild__cmd-group">
        <div className="fbuild__cmd-head">
          <span className="fbuild__cmd-title">{t('1. Install SV Cloud CLI (Mandatory)')}</span>
        </div>
        <CopyField
          label={t('CLI install and sign-in command')}
          value="npm install -g svcloud && svcloud login"
        />
      </div>

      <div className="fbuild__cmd-group">
        <div className="fbuild__cmd-head">
          <span className="fbuild__cmd-title">{t('2. Clone repository')}</span>
          <SegmentedControl<'git' | 'gh'>
            label={t('Clone command')}
            value={cloneMethod}
            options={[
              { value: 'git', label: 'git' },
              { value: 'gh', label: 'gh' },
            ]}
            onChange={onCloneMethod}
          />
        </div>
        <CopyField
          label={cloneMethod === 'git' ? t('Git clone command') : t('GitHub CLI clone command')}
          value={
            cloneMethod === 'git'
              ? `git clone https://github.com/${repoFullName}.git`
              : `gh repo clone ${repoFullName}`
          }
        />
      </div>

      <div className="fbuild__cmd-group">
        <div className="fbuild__cmd-head">
          <span className="fbuild__cmd-title">{t('3. Prompt for your coding agent')}</span>
        </div>
        <CopyField
          label={t('Setup prompt')}
          value="Read SVAGENTS.md and set up the repository for SV Cloud."
        />
      </div>

      <div className="fbuild__actions">
        <Button variant="outline" onClick={onGoToProject}>
          {t('Go to your project')}
        </Button>
      </div>
    </section>
  );
}

function RealFirstBuild() {
  const account = useAccount();
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const runId = params.get('run');
  const projectId = params.get('project');
  const slug = params.get('slug');
  const retryInput = (location.state as RetryInput | null) ?? null;
  const svgit = params.get('host') === 'svgit';

  const [run, setRun] = useState<Run | null>(null);
  const [runError, setRunError] = useState<string | null>(null);
  const [project, setProject] = useState<Project | null>(null);
  const [cloneMethod, setCloneMethod] = useState<'git' | 'gh'>('git');
  const [retrying, setRetrying] = useState(false);
  const autoRetriedRef = useRef(false);
  const startedAtRef = useRef(Date.now());

  // The project row is the truth once it lands; the retry state is what we
  // have before it does. Both were being reached for inline, twice, in the
  // clone command alone, and the one-shot prompt needs the same answer.
  const repoFullName =
    (project?.repo ? `${project.repo.owner}/${project.repo.name}` : null) ??
    retryInput?.repoFullName ??
    'owner/repo';

  // One retry, re-running the same connect call ConnectCode made. Works for
  // both a manual click and the auto-retry below; `auto` just skips it
  // silently instead of surfacing the failed run first.
  function retry() {
    if (!retryInput || !slug || retrying) return;
    setRetrying(true);
    connectProject({
      name: retryInput.name,
      codeHost: retryInput.codeHost,
      repoFullName: retryInput.repoFullName,
      githubInstallationId: retryInput.githubInstallationId,
      requestedSubdomain: slug,
    })
      .then((result) => {
        startedAtRef.current = Date.now();
        setRun(null);
        setProject(null);
        navigate(
          `/new/building?project=${result.projectId}&run=${result.runId ?? ''}&slug=${result.slug}`,
          { replace: true, state: retryInput },
        );
      })
      .catch(() => setRetrying(false));
  }

  // Setup failing is our problem, not the user's: try once, quietly, before
  // ever showing a failure screen. Only for codes that mean "the platform
  // hiccuped", never for a failure a retry can't fix (bad repo state, taken
  // subdomain, ...).
  useEffect(() => {
    if (run?.status !== 'failed' || autoRetriedRef.current) return;
    autoRetriedRef.current = true;
    if (retryInput && run.error && TRANSIENT_RUN_ERROR_CODES.has(run.error.code)) retry();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- retry() is stable enough for a one-shot effect; re-running it on every dep change would defeat the "once" guard
  }, [run?.status]);

  // Poll the run until it settles. Tiered backoff: fast while a person is
  // likely still watching, slower once the run has been going a while.
  // Mirrors repo-orchestration's loop-web polling (provisioning-checklist.tsx).
  useEffect(() => {
    if (!runId) return;
    let stale = false;
    let timer: number | undefined;

    function nextDelay(): number {
      const elapsed = Date.now() - startedAtRef.current;
      if (elapsed < 15_000) return 1000;
      if (elapsed < 60_000) return 2000;
      return 5000;
    }

    async function poll() {
      if (timer !== undefined) {
        window.clearTimeout(timer);
        timer = undefined;
      }
      try {
        const next = await getRun(runId!);
        if (stale) return;
        setRun(next);
        if (next.status === 'queued' || next.status === 'running') {
          timer = window.setTimeout(poll, nextDelay());
        }
      } catch {
        if (!stale) setRunError(t('Lost track of the setup. Reload this page to check on it.'));
      }
    }

    function onVisible() {
      if (document.visibilityState === 'visible') void poll();
    }

    void poll();
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      stale = true;
      document.removeEventListener('visibilitychange', onVisible);
      if (timer) window.clearTimeout(timer);
    };
  }, [runId]);

  // Once the run has succeeded, poll the project itself until the bundle is
  // unpacked and the first real deploy has gone out, that's what makes
  // status 'live' true (real-api.ts's mapProjectStatus).
  useEffect(() => {
    if (run?.status !== 'succeeded' || !slug) return;
    let stale = false;
    let timer: number | undefined;

    async function poll() {
      try {
        const next = await getProject(slug!);
        if (stale) return;
        setProject(next);
        if (next && next.status !== 'live') {
          timer = window.setTimeout(poll, 3000);
        }
      } catch {
        // Best-effort: the run already succeeded, so leave the "finish
        // setting up" guidance on screen rather than surfacing a new error.
      }
    }
    void poll();

    return () => {
      stale = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [run?.status, slug]);

  useEffect(() => {
    if (run?.status) {
      captureEvent('first_build_status_updated', {
        status: run.status,
        slug,
        projectId,
        error: run.error?.message,
      });
    }
  }, [run?.status, slug, projectId, run?.error?.message]);

  // Deployed is not the same as reachable: a new address can refuse HTTPS for
  // a few minutes while its certificate is issued (use-address-answers.ts), so
  // "live" here means the server says so AND the address answers.
  const deployed = project?.status === 'live';
  const webAddress = project?.environments.production.webAddress ?? null;
  const answers = useAddressAnswers(webAddress, deployed);
  const live = deployed && answers;

  useEffect(() => {
    if (live) {
      captureEvent('first_build_went_live', {
        slug,
        projectName: project?.name,
      });
    }
  }, [live, slug, project?.name]);

  // The finish-setting-up block is the screen's real fork: the run settled
  // but the app is not live, so what the person does next is copy something.
  // Fired once when that block appears, so the copy events below it have a
  // denominator.
  useEffect(() => {
    if (run?.status === 'succeeded' && project?.status !== 'live') {
      captureEvent('first_build_setup_handoff_viewed', {
        slug,
        projectId,
        has_repo: Boolean(project?.repo),
      });
    }
  }, [run?.status, project?.status, project?.repo, slug, projectId]);

  const runActive = run?.status === 'queued' || run?.status === 'running';

  // The retrying-step rule lives in lib/run-ledger.ts, shared with the
  // project overview's own provisioning ledger so the two cannot drift.
  const ledgerSteps: NarrationStep[] = runLedgerSteps(run);

  const longRunning = runActive && Date.now() - startedAtRef.current >= LONG_RUNNING_MS;

  function goToProject() {
    if (slug) navigate(`/p/${encodeURIComponent(slug)}`);
  }

  if (!runId || !projectId || !slug) {
    return (
      <div className="fbuild">
        <Notice tone="attention">
          <p>{t('This page needs a project to watch. Start a new project from the beginning.')}</p>
        </Notice>
        <div className="fbuild__actions">
          <Button variant="solid" onClick={() => navigate('/new')}>
            {t('Start over')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="fbuild">
      <StepDots
        label={t('Setting up your app')}
        current={2}
        steps={ONBOARDING_STEPS.map((step) => t(step))}
      />

      <header className="fbuild__head">
        <h1 className="fbuild__title">
          {live
            ? t('{project} is live', { project: project?.name ?? '' })
            : deployed
              ? t('{project} is almost live', { project: project?.name ?? '' })
              : run?.status === 'succeeded'
                ? t('Your first build is running')
                : t('Setting up your app')}
        </h1>
        {deployed && !live ? (
          <p className="fbuild__lede">
            {t('Your app is built. Its web address starts working in a few minutes, and this page will say when.')}
          </p>
        ) : !live && run?.status !== 'failed' ? (
          <p className="fbuild__lede">{t('Putting your app on the internet.')}</p>
        ) : null}
      </header>

      {runError ? (
        <Notice tone="attention">
          <p>{runError}</p>
        </Notice>
      ) : null}

      {run ? (
        <StepLedger
          steps={ledgerSteps}
          longRunning={longRunning}
          onLeave={goToProject}
          longRunningNote={t(
            'This is taking longer than usual. Setup keeps running even if you close this page. Check your project page for the result.',
          )}
          leaveLabel={t('Go to your project')}
          error={
            run.status === 'failed' && !retrying ? (
              <ErrorBlock
                summary={t("Setup didn't finish.")}
                cause={run.error?.message ?? t('Something went wrong while setting up your project.')}
                location={run.error?.code}
                fixRequest={t('Setting up my SV Cloud project failed: {error}', {
                  error: run.error?.message ?? t('unknown error'),
                })}
                contextAction={
                  retryInput
                    ? { label: t('Try again'), onAct: retry }
                    : { label: t('Go to your project'), onAct: goToProject }
                }
              />
            ) : undefined
          }
        />
      ) : null}

      {/* The run settling is not the app going live, and the gap between them
          is where this screen used to over-promise. What actually happens
          next depends on the repo: an empty one got the starter at its real
          paths, so GitHub Actions is already building it and this page keeps
          polling until the project says live on its own. A repo that already
          had code got the starter nested and inert, and a coding agent has
          to unpack it first. What follows is what a person needs either way,
          as one prompt that does the whole thing and the three steps it
          stands for, and the sentence above them says which case they are
          in rather than assuming the second. */}
      {run?.status === 'succeeded' && (!live || svgit) ? (
        <SetupHandoff
          webAddress={project?.environments.production.webAddress ?? null}
          repoFullName={repoFullName}
          cloneMethod={cloneMethod}
          onCloneMethod={setCloneMethod}
          onGoToProject={goToProject}
          svgitSlug={svgit && slug ? slug : undefined}
          setupFirst={account.cliConnected === false}
        />
      ) : null}

      {live && project && !svgit ? (
        <section className="fbuild__payoff" aria-label={t('Your web address')}>
          <CopyField label={t('Web address')} value={project.environments.production.webAddress} size="lg" />
          <div className="fbuild__actions">
            <Button
              variant="solid"
              onClick={() =>
                window.open(project.environments.production.webAddress, '_blank', 'noopener,noreferrer')
              }
            >
              {t('Visit your app')}
            </Button>
            <Button variant="outline" onClick={goToProject}>
              {t('Go to your project')}
            </Button>
          </div>
          <p className="fbuild__push">
            {t('Every time you push to main, this updates automatically.')}
          </p>
        </section>
      ) : null}

      <span className="fbuild__say" role="status" aria-live="polite">
        {live ? t('{project} is live', { project: project?.name ?? '' }) : ''}
      </span>
    </div>
  );
}

export default function FirstBuild() {
  if (!USE_MOCK) return <RealFirstBuild />;
  return <MockFirstBuild />;
}

function MockFirstBuild() {
  const account = useAccount();
  const now = useTicker();
  const navigate = useNavigate();
  const location = useLocation();

  const timelines = useDemoStore((s) => s.timelines);
  const startTimeline = useDemoStore((s) => s.startTimeline);
  const nextBuildFails = useDemoStore((s) => s.prefs.nextBuildFails);

  const draft = NEW_PROJECT_DRAFT;
  const screenState = readState(location.search);
  const svgitHost = new URLSearchParams(location.search).get('host') === 'svgit';
  const handoff = screenState === 'handoff' || svgitHost;
  const revisit = screenState === 'live' || handoff;

  const [cloneMethod, setCloneMethod] = useState<'git' | 'gh'>('git');

  const outcome: TimelineOutcome =
    screenState === 'failed' || nextBuildFails ? 'fail@build' : 'success';

  // Start a run on arrival, and start a fresh one when the demanded ending
  // changes, which is what makes the dock switch and the state parameter feel
  // immediate. A run that is already the ending being asked for is left alone,
  // so a refresh mid build resumes rather than restarting: the engine reads
  // the clock, and the clock did not reset.
  useEffect(() => {
    if (revisit) return;
    const running = useDemoStore.getState().timelines.find((line) => line.id === TIMELINE_ID);
    if (running && running.outcome === outcome) return;
    startTimeline({
      id: TIMELINE_ID,
      kind: 'first-build',
      scriptId: 'first-build',
      subjectId: draft.slug,
      startedAt: Date.now(),
      outcome,
    });
  }, [revisit, outcome, startTimeline, draft.slug]);

  const timeline = timelines.find((line) => line.id === TIMELINE_ID);
  const state = timeline ? stateOf(timeline, now) : null;

  const succeeded = revisit || state?.status === 'succeeded';
  const failed = !revisit && state?.status === 'failed';
  const running = !revisit && !succeeded && !failed;

  const heading = succeeded && handoff
    ? t('Finish setting up')
    : succeeded
    ? t('{project} is live', { project: draft.name })
    : state && state.stepIndex >= BUILD_INDEX
      ? t('Connecting all the wires')
      : t('Setting up your app');

  // Created silently, announced after the fact. The moment both resources
  // exist, and not one step before.
  const announced =
    succeeded ||
    (state?.stepStatuses[0] === 'succeeded' && state?.stepStatuses[1] === 'succeeded');

  const logLines = state?.logsUpTo ?? [];

  function goToProject() {
    navigate(`/p/${encodeURIComponent(draft.slug)}`);
  }

  function tryAgain() {
    startTimeline({
      id: TIMELINE_ID,
      kind: 'first-build',
      scriptId: 'first-build',
      subjectId: draft.slug,
      startedAt: Date.now(),
      // The retry honors the dock rather than the ending that just happened,
      // so "Try again" in a demo actually recovers instead of failing forever.
      outcome: nextBuildFails ? 'fail@build' : 'success',
    });
  }

  const buildOutput = (
    <div className="fbuild__log">
      {logLines.map((line, index) => (
        <Fragment key={`${line.at}-${index}`}>
          <span className="fbuild__at">{stamp(line.at)}</span>
          <span className="fbuild__line">{line.line}</span>
        </Fragment>
      ))}
    </div>
  );

  return (
    <div className="fbuild">
      <StepDots
        label={t('Setting up your app')}
        current={2}
        steps={ONBOARDING_STEPS.map((step) => t(step))}
      />

      <header className="fbuild__head">
        <h1 className="fbuild__title">{heading}</h1>
        {running ? <p className="fbuild__lede">{t('Putting your app on the internet.')}</p> : null}
      </header>

      {timeline && !revisit ? (
        <AsyncNarration
          timeline={timeline}
          forceLongRunning={screenState === 'slow' && running}
          onLeave={goToProject}
          error={
            <ErrorBlock
              summary={t('Your code has a spelling mistake in a function name.')}
              cause={t(
                "Your database and file storage are ready. Only the build didn't finish. One file calls a function named formatServings, but the file that defines it spells it formatServing, so the build stopped before your app could go online.",
              )}
              location={draft.buildFailure.location}
              fixRequest={draft.buildFailure.fixRequest}
              contextAction={{ label: t('Try again'), onAct: tryAgain }}
              rawLabel={t('Show the build log')}
              raw={asText(logLines)}
            />
          }
        />
      ) : null}

      {announced && !failed ? (
        <p className="fbuild__note">
          {t(
            'We set up a database and file storage for you. Your app is already connected to both.',
          )}
        </p>
      ) : null}

      {succeeded && handoff ? (
        <SetupHandoff
          webAddress={draft.webAddress}
          repoFullName={`${draft.slug}-owner/${draft.slug}`}
          cloneMethod={cloneMethod}
          onCloneMethod={setCloneMethod}
          onGoToProject={goToProject}
          svgitSlug={svgitHost ? draft.slug : undefined}
          setupFirst={account.cliConnected === false}
        />
      ) : null}

      {succeeded && !handoff ? (
        <section className="fbuild__payoff" aria-label={t('Your web address')}>
          <CopyField label={t('Web address')} value={draft.webAddress} size="lg" />

          <div className="fbuild__actions">
            <Button
              variant="solid"
              onClick={() => window.open(draft.webAddress, '_blank', 'noopener,noreferrer')}
            >
              {t('Visit your app')}
            </Button>
            <Button variant="outline" onClick={goToProject}>
              {t('Go to your project')}
            </Button>
          </div>

          <p className="fbuild__push">
            {t('Every time you push to main, this updates automatically.')}
          </p>
        </section>
      ) : null}

      {/* The build output, appended last, under the payoff. The disclosure
          renders its own trigger in Simple and the same content already open
          in Advanced, so turning the switch on adds nothing above it and moves
          nothing. When the build did not work, the error block's own raw
          disclosure already owns the log, and one screen showing the same log
          twice reads as a bug. */}
      {!failed && logLines.length > 0 ? (
        <AdvancedDisclosure label={t('Show the build output')}>{buildOutput}</AdvancedDisclosure>
      ) : null}

      {/* The ledger announces each step as it runs. The ending is the one thing
          it cannot announce, because by then no step is running. */}
      <span className="fbuild__say" role="status" aria-live="polite">
        {succeeded && !handoff ? t('{project} is live', { project: draft.name }) : ''}
      </span>
    </div>
  );
}
